import { DRIVES_URL, ME_URL } from '../constants';
import { apiSlice } from './apiSlice';

// `mine: true` targets the signed-in user's drives (/api/me/drives) — same response
// shape as the public endpoints, but stats come from the user's benchmarks only.
// Those also depend on claimed benchmarks, so they're tagged 'Benchmark' too.
const driveTags = (result, error, arg) => (arg?.mine ? ['Drive', 'Benchmark'] : ['Drive']);

export const drivesApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getDrives: builder.query({
            query: ({ mine, userId, page = 1, pageSize = 10, sortBy, make, type, excludeUsb } = {}) => {
                const params = new URLSearchParams({ page, pageSize });
                if (sortBy && sortBy !== 'model_asc') params.set('sortBy', sortBy);
                if (make && make !== 'All') params.set('make', make);
                if (type && type !== 'All') params.set('type', type);
                if (excludeUsb) params.set('excludeUsb', 'true');

                if (mine) {
                    return { url: `${ME_URL}/drives?${params}` };
                }
                if (userId) {
                    return { url: `${DRIVES_URL}/user/${userId}/all_drives?${params}` };
                }
                return { url: `${DRIVES_URL}/all_drives?${params}` };
            },
            keepUnusedDataFor: 300,
            providesTags: driveTags,
        }),
        getDriveById: builder.query({
            query: ({ mine, userId, driveId }) => {
                if (mine) {
                    return { url: `${ME_URL}/drives/drive/${driveId}` };
                }
                if (userId) {
                    return { url: `${DRIVES_URL}/user/${userId}/all_drives/drive/${driveId}` };
                }
                return { url: `${DRIVES_URL}/all_drives/drive/${driveId}` };
            },
            keepUnusedDataFor: 3600,
            providesTags: driveTags,
        }),
        getDriveBenchmarks: builder.query({
            query: ({ mine, userId, driveId, limit = 100, standard, profile, attachment }) => {
                const params = new URLSearchParams({ limit });
                if (standard !== undefined) params.set('standard', String(standard));
                if (profile) params.set('profile', profile);
                if (attachment) params.set('attachment', attachment);
                if (mine) {
                    return { url: `${ME_URL}/drives/drive/${driveId}/benchmarks?${params}` };
                }
                if (userId) {
                    return { url: `${DRIVES_URL}/user/${userId}/all_drives/drive/${driveId}/benchmarks?${params}` };
                }
                return { url: `${DRIVES_URL}/all_drives/drive/${driveId}/benchmarks?${params}` };
            },
            keepUnusedDataFor: 300,
            providesTags: (result, error, arg) => (arg?.mine ? ['Benchmark'] : []),
        }),
        syncDrives: builder.mutation({
            query: () => ({
                url: `${DRIVES_URL}/sync`,
                method: 'POST',
            }),
            invalidatesTags: ['Drive'],
        }),
        rebuildDrives: builder.mutation({
            query: () => ({
                url: `${DRIVES_URL}/rebuild`,
                method: 'POST',
            }),
            invalidatesTags: ['Drive'],
        }),
        deleteDrive: builder.mutation({
            query: ({ userId, driveId }) => ({
                url: `${DRIVES_URL}/user/${userId}/all_drives/drive/${driveId}/delete_drive`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Drive'],
        }),
    }),
});

export const {
    useGetDrivesQuery,
    useGetDriveByIdQuery,
    useGetDriveBenchmarksQuery,
    useSyncDrivesMutation,
    useRebuildDrivesMutation,
    useDeleteDriveMutation,
} = drivesApiSlice;